<template>
  <!-- Zero-size host — moved to document.body in mounted() -->
  <div class="lb-host">
    <transition name="lb-fade">
      <div
        v-if="show"
        class="lb-overlay"
        :class="isDark ? 'lb-dark' : 'lb-light'"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        ref="root"
        @keydown.esc="close"
        @click.self="close"
      >
        <!-- Card -->
        <div class="lb-card">
          <!-- ── Header ── -->
          <div class="lb-header">
            <span class="lb-title">{{ title || 'Image Preview' }}</span>
            <button class="lb-close-btn" type="button" aria-label="Close" @click="close">
              <svg
                width="16" height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <!-- ── Body ── -->
          <div class="lb-body">
            <!-- No source -->
            <div v-if="!resolvedSrc" class="lb-state">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" class="lb-state-icon">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <span>No image available</span>
            </div>

            <!-- Load error -->
            <div v-else-if="loadError" class="lb-state">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" class="lb-state-icon lb-state-icon--error">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="9" x2="15" y2="15" />
                <line x1="15" y1="9" x2="9" y2="15" />
              </svg>
              <span>Could not load image</span>
              <a :href="resolvedSrc" target="_blank" rel="noopener" class="lb-open-link">
                Open in new tab ↗
              </a>
            </div>

            <!-- Image wrapper -->
            <div v-else class="lb-img-wrap">
              <!-- Spinner -->
              <div v-show="loading" class="lb-spinner-wrap">
                <div class="lb-spinner"></div>
              </div>
              <!-- Image (v-show so onLoad / onError always fires) -->
              <img
                v-show="!loading && !loadError"
                ref="imgEl"
                :key="resolvedSrc"
                :src="resolvedSrc"
                :alt="title"
                class="lb-img"
                @load="onLoad"
                @error="onError"
              />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { resolveUploadUrl } from '@/helpers/mediaUrl';

export default {
  name: 'PremiumImageLightbox',

  props: {
    /** v-model */
    value: { type: Boolean, default: false },
    /** Full URL or relative path under /uploads/ */
    imageSrc: { type: String, default: '' },
    title: { type: String, default: 'Image Preview' },
  },

  data() {
    return {
      loading: false,
      loadError: false,
    };
  },

  computed: {
    show: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },

    isDark() {
      return !!(
        this.$store &&
        this.$store.state.theme &&
        this.$store.state.theme.isDark
      );
    },

    resolvedSrc() {
      const src = (this.imageSrc || '').trim();
      if (!src) return '';
      if (src.startsWith('http://') || src.startsWith('https://')) return src;
      return resolveUploadUrl(src) || '';
    },
  },

  watch: {
    resolvedSrc() {
      this.resetLoadState();
      this.syncImageFromCache();
    },

    show(open) {
      if (open) {
        this.resetLoadState();
        // Lock body scroll
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
          if (this.$refs.root) this.$refs.root.focus();
          this.syncImageFromCache();
        });
      } else {
        document.body.style.overflow = '';
      }
    },
  },

  methods: {
    close() {
      this.show = false;
    },

    resetLoadState() {
      this.loadError = false;
      this.loading = !!this.resolvedSrc;
    },

    /**
     * If the browser already cached the image, the load/error events won't
     * re-fire — so we sync the state manually after render.
     */
    syncImageFromCache() {
      if (!this.show || !this.resolvedSrc) return;
      this.$nextTick(() => {
        const img = this.$refs.imgEl;
        if (img && img.complete) {
          if (img.naturalWidth > 0) this.onLoad();
          else this.onError();
        }
      });
    },

    onLoad() {
      this.loading = false;
      this.loadError = false;
    },

    onError() {
      this.loading = false;
      this.loadError = true;
    },
  },

  mounted() {
    // Move host element to <body> so it's never clipped by any ancestor's
    // overflow/transform, and is unaffected by PremiumModalForm's global CSS.
    document.body.appendChild(this.$el);
  },

  beforeDestroy() {
    // Restore scroll lock just in case
    document.body.style.overflow = '';
    // Detach from body before Vue tears down the component
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
};
</script>

<style>
/* ── Host: zero-size placeholder that stays in the Feedback component DOM ── */
.lb-host {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  overflow: visible;
  pointer-events: none;
}

/* ── Full-screen overlay (appended to body) ── */
.lb-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
  outline: none;
  font-family: 'Inter', sans-serif;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* ── Light mode — uses the app's light theme variables ── */
.lb-overlay.lb-light {
  background: rgba(15, 23, 42, 0.45);
}
.lb-overlay.lb-light .lb-card {
  background: var(--card-bg, #ffffff);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.16);
}
.lb-overlay.lb-light .lb-header {
  background: var(--bg-primary, #f1f5f9);
  border-bottom: 1px solid var(--border-color, rgba(0,0,0,0.08));
}
.lb-overlay.lb-light .lb-title {
  color: var(--text-primary, #1e293b);
}
.lb-overlay.lb-light .lb-close-btn {
  border: 1px solid var(--border-color, rgba(0,0,0,0.12));
  background: var(--bg-primary, #f1f5f9);
  color: var(--text-secondary, #64748b);
}
.lb-overlay.lb-light .lb-body {
  background: var(--bg-primary, #f1f5f9);
}
.lb-overlay.lb-light .lb-img {
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.18);
}
.lb-overlay.lb-light .lb-state {
  color: var(--text-secondary, #64748b);
}
.lb-overlay.lb-light .lb-state-icon {
  color: var(--text-secondary, #64748b);
  opacity: 0.6;
}
.lb-overlay.lb-light .lb-state-icon--error {
  color: #ef4444;
  opacity: 0.85;
}

/* ── Dark mode — uses the app's dark theme variables ── */
.lb-overlay.lb-dark {
  background: rgba(15, 23, 42, 0.65);
}
.lb-overlay.lb-dark .lb-card {
  background: var(--card-bg, #1a2035);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
}
.lb-overlay.lb-dark .lb-header {
  background: var(--bg-primary, #141928);
  border-bottom: 1px solid var(--border-color, rgba(255,255,255,0.06));
}
.lb-overlay.lb-dark .lb-title {
  color: var(--text-primary, #f1f5f9);
}
.lb-overlay.lb-dark .lb-close-btn {
  border: 1px solid var(--border-color, rgba(255,255,255,0.12));
  background: var(--bg-primary, rgba(255,255,255,0.06));
  color: var(--text-secondary, #94a3b8);
}
.lb-overlay.lb-dark .lb-body {
  background: var(--bg-primary, #141928);
}
.lb-overlay.lb-dark .lb-img {
  box-shadow: 0 20px 70px rgba(0, 0, 0, 0.6);
}
.lb-overlay.lb-dark .lb-state {
  color: var(--text-secondary, #94a3b8);
}
.lb-overlay.lb-dark .lb-state-icon {
  color: var(--text-secondary, #94a3b8);
  opacity: 0.5;
}
.lb-overlay.lb-dark .lb-state-icon--error {
  color: #f87171;
  opacity: 0.85;
}

/* ── Card (90% width and height, centered) ── */
.lb-overlay .lb-card {
  width: 90%;
  height: 90%;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
}

/* ── Header (base — theme overrides above) ── */
.lb-overlay .lb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  flex-shrink: 0;
}

.lb-overlay .lb-title {
  font-size: 15px;
  font-weight: 700;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.01em;
}

.lb-overlay .lb-close-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s, transform 0.22s;
  flex-shrink: 0;
  padding: 0;
  line-height: 0;
}

.lb-overlay .lb-close-btn:hover {
  background: #ef4444 !important;
  border-color: #ef4444 !important;
  color: #fff !important;
  transform: rotate(90deg);
}

/* ── Body ── */
.lb-overlay .lb-body {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* ── Image wrap ── */
.lb-overlay .lb-img-wrap {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  box-sizing: border-box;
}

.lb-overlay .lb-img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  border-radius: 6px;
}

/* ── Loading spinner ── */
.lb-overlay .lb-spinner-wrap {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lb-overlay .lb-spinner {
  width: 44px;
  height: 44px;
  border: 3px solid rgba(242, 101, 34, 0.25);
  border-top-color: #F26522;
  border-radius: 50%;
  animation: lb-spin 0.75s linear infinite;
}

@keyframes lb-spin {
  to { transform: rotate(360deg); }
}

/* ── Empty / Error state (base — theme overrides above) ── */
.lb-overlay .lb-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  padding: 32px;
  font-family: 'Inter', sans-serif;
}

.lb-overlay .lb-open-link {
  color: #F26522;
  font-size: 13px;
  text-decoration: underline;
  transition: color 0.15s;
}

.lb-overlay .lb-open-link:hover {
  color: #ff8c52;
}

/* ── Transition ── */
.lb-fade-enter-active,
.lb-fade-leave-active {
  transition: opacity 0.25s ease;
}
.lb-fade-enter-active .lb-card,
.lb-fade-leave-active .lb-card {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.lb-fade-enter,
.lb-fade-leave-to {
  opacity: 0;
}
.lb-fade-enter .lb-card,
.lb-fade-leave-to .lb-card {
  transform: scale(0.95);
}
</style>
